/**
 * UserRedirect Screen
 *
 * Handles the permanent URL route: /u/:profileUserId
 * Now uses onSessionFound callback to route in-app (no page reload).
 *
 * Flow:
 * 1. Anon visits permanent URL (e.g., /u/abc123)
 * 2. Check localStorage for existing connection with this profileUserId
 * 3. Check Supabase connections table for existing connection (handles stale localStorage)
 * 4. Check active_sessions for a live QR session
 * 5. If found: claim its room (052 — one exchange per code at a time), then
 *    call onSessionFound(chatRoomId) to route in-app
 * 6. If none found: shows "Not Available" message
 * 7. If someone else holds the room: shows the busy message
 *
 * Also reached already busy: from App.js's prefetch path (a guest) and from a
 * profile holder's scan (qrPXPExchange), which pass `busyName`. A profile
 * holder (`inApp`) gets "Back to People"; a guest's screen has no button, like
 * Not Available — they close the tab.
 */

import { useEffect, useState, useRef } from 'react';
import { HandWaving } from '@phosphor-icons/react/dist/ssr/HandWaving';
import { claimQrRoom, getActiveSession, getExistingConnection } from '../services/activeSession';
import { getAnonUserId } from '../utils/identityUtils';
import { logConnectionError, logEvent } from '../services/analytics/eventLogger';
import { ButtonRounded } from '../components/UISystem/Buttons';
import { useNavigation } from '../contexts/NavigationContext';
import { isNewExperience } from '../utils/newExperience';
import { hideInstantSplash } from '../utils/instantSplash';
import { busyTitle } from '../utils/contactUtils';

// The busy screen's title (Mark, 2026-10-21) — shared with the code page
export { busyTitle };

// Generate unique ID per page load for debugging multiple loads
const pageLoadId = Math.random().toString(36).substring(2, 8);
console.log(`[UserRedirect] Page load ID: ${pageLoadId}`);

const UserRedirect = ({ profileUserId, onSessionFound, busyName = null, inApp = false }) => {
  // 'loading' | 'not_active' | 'busy' | 'error'
  const [status, setStatus] = useState(busyName ? 'busy' : 'loading');
  const [hostName, setHostName] = useState(busyName);
  const { navigateTo } = useNavigation();
  const hasAttempted = useRef(false);

  useEffect(() => {
    // Arrived already busy: nothing to look up
    if (busyName) return;
    // Prevent double-execution in StrictMode
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const lookupAndRoute = async () => {
      console.log('[UserRedirect] Looking up session for:', profileUserId);

      if (!profileUserId) {
        console.error('[UserRedirect] No profileUserId provided');
        logConnectionError('user_redirect', {
          message: 'No profileUserId provided',
          code: 'MISSING_PROFILE_ID',
          profileUserId: null,
          pageLoadId,
        });
        setStatus('error');
        return;
      }

      // Helper: route in-app via callback, or fall back to redirect if no callback
      const routeToSession = (chatRoomId, source) => {
        if (onSessionFound) {
          console.log(`[UserRedirect] Routing in-app (${source}):`, chatRoomId);
          onSessionFound(chatRoomId);
        } else {
          // Defensive fallback — shouldn't reach in normal flow
          console.log(`[UserRedirect] No callback, redirecting (${source}):`, chatRoomId);
          const timestamp = Date.now();
          const redirectUrl = `${window.location.origin}?s=a&c=${chatRoomId}&t=${timestamp}`;
          window.location.replace(redirectUrl);
        }
      };

      // Check localStorage for an existing connection with this Profile user.
      // If Anon already connected previously, go straight to that chat —
      // no need to check active_sessions (which requires QR to be open).
      const RECEIVED_PREFIX = 'received_profile_data_';
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(RECEIVED_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data?.profileUserId === profileUserId) {
              const chatRoomId = key.replace(RECEIVED_PREFIX, '');
              console.log('[UserRedirect] Found existing connection in localStorage:', chatRoomId);
              routeToSession(chatRoomId, 'localStorage');
              return;
            }
          } catch {
            // Skip malformed entries
          }
        }
      }

      // Check Supabase connections table for existing connection.
      // Handles case where localStorage is stale/missing but connection exists in DB
      // (e.g., page idle, iOS new browser context with preserved anon_user_id).
      const anonUserId = getAnonUserId();
      if (anonUserId) {
        const existing = await getExistingConnection(anonUserId, profileUserId);
        if (existing?.chat_room_id) {
          console.log('[UserRedirect] Found existing connection in DB:', existing.chat_room_id);
          routeToSession(existing.chat_room_id, 'connections_table');
          return;
        }
      }

      // No existing connection — check active_sessions for a live QR scan
      // Try up to 2 times with immediate retry on failure
      const maxAttempts = 2;
      let lastError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const session = await getActiveSession(profileUserId);

          if (session?.chat_room_id) {
            console.log('[UserRedirect] Found active session:', session.chat_room_id);
            const claim = await claimQrRoom(session.chat_room_id);
            if (claim === 'claimed') {
              routeToSession(session.chat_room_id, 'active_sessions');
            } else if (claim === 'busy') {
              setHostName(session.name);
              setStatus('busy');
            } else if (claim === 'closed') {
              setStatus('not_active');
            } else {
              // The claim call failed (postScannerRpc logged it after its retries)
              setStatus('error');
            }
            return;
          } else {
            // No active session - Profile user isn't on QR screen
            console.log('[UserRedirect] No active session found');
            setStatus('not_active');
            return; // Expected state - exit
          }
        } catch (err) {
          lastError = err;
          console.error(`[UserRedirect] Attempt ${attempt}/${maxAttempts} failed:`, {
            error: err.message,
            code: err.code,
            details: err.details,
            hint: err.hint,
            profileUserId,
          });

          if (attempt < maxAttempts) {
            console.log('[UserRedirect] Retrying immediately...');
          }
        }
      }

      // All attempts failed - only log to Supabase after ALL retries exhausted
      console.error('[UserRedirect] All attempts failed. Last error:', lastError);
      logConnectionError('user_redirect', {
        message: lastError?.message,
        code: lastError?.code,
        details: lastError?.details,
        hint: lastError?.hint,
        profileUserId,
        attempts: maxAttempts,
        pageLoadId,
      });
      setStatus('error');
    };

    lookupAndRoute();
  }, [profileUserId, onSessionFound, busyName]);

  // This screen owns the instant splash's hide (ScreenRouter SPLASH_OWNED_SCREENS):
  // a found session hands over to the exchange flow, which hides it; an answer
  // shown here (Not Available, busy, error) hides it now — it used to stay up
  // until index.html's 8 s failsafe, a white spinner over the answer
  useEffect(() => {
    if (status !== 'loading') hideInstantSplash();
    // How often two people meet one code at once (052's turn-aways)
    if (status === 'busy') logEvent('qr_room_busy', { hostProfileUserId: profileUserId, inApp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Loading state - show connecting spinner
  if (status === 'loading') {
    return (
      <div className="user-redirect-screen">
        <div className="redirect-content">
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Not active - Profile user isn't sharing right now
  if (status === 'not_active') {
    return (
      <div className="user-redirect-screen">
        <div className="redirect-content">
          <div className="status-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="32" cy="32" r="30" stroke="#666" strokeWidth="4" />
              <path d="M20 32H44" stroke="#666" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="status-title">Not Available</h2>
          <p className="status-message">This person isn&apos;t sharing their contact right now.</p>
          <p className="status-hint">Ask them to open their QR code</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Busy - the code is mid-exchange with someone else (Mark, 2026-10-21):
  // Not Available's layout, no hint; a waving hand (Mark's call) instead of
  // its icon, at the same 64px and line weight (regular: 4px at 64)
  if (status === 'busy') {
    return (
      <div className="user-redirect-screen">
        <div className="redirect-content">
          <div className="status-icon" data-icon="hand-waving">
            <HandWaving size={64} color="#666" weight="regular" />
          </div>
          <h2 className="status-title">{busyTitle(hostName)}</h2>
          <p className="status-message">Try again in a moment.</p>
          {inApp ? (
            <div className="busy-back">
              <ButtonRounded
                text="Back to People"
                variant="secondary"
                onClick={() =>
                  navigateTo(
                    isNewExperience() ? 'new-people' : 'profile-connections-list',
                    {},
                    true
                  )
                }
              />
            </div>
          ) : null}
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Error state - gracious message prompting to scan again
  if (status === 'error') {
    return (
      <div className="user-redirect-screen">
        <div className="redirect-content">
          <div className="status-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="32" cy="32" r="30" stroke="#666" strokeWidth="4" />
              <circle cx="32" cy="24" r="4" fill="#666" />
              <path d="M32 34V44" stroke="#666" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="status-title">Connection Issue</h2>
          <p className="status-message">We couldn&apos;t connect</p>
          <p className="status-hint">Please scan the QR again!</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return null;
};

const styles = `
  .user-redirect-screen {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    padding: 24px;
  }

  .redirect-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 300px;
  }

  .spinner-container {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-top-color: #4a4a4a;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .status-icon {
    margin-bottom: 24px;
  }

  .status-title {
    color: #1a1a1a;
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 12px 0;
  }

  .status-message {
    color: #666;
    font-size: 16px;
    line-height: 1.5;
    margin: 0 0 8px 0;
  }

  .status-hint {
    color: #999;
    font-size: 14px;
    margin: 0;
  }

  .busy-back {
    margin-top: 24px;
  }

  .retry-button {
    margin-top: 24px;
    padding: 12px 32px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 8px;
    color: #333;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .retry-button:hover {
    background: #eee;
  }

  .retry-button:active {
    background: #e0e0e0;
  }
`;

export default UserRedirect;
